/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { useScript } from "@awc/App/Hooks/useScript";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { getSettingsCollection } from "@awc/App/Store/Selectors/SettingsSelectors";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const oneTrustSettingsCollection = getSettingsCollection(state).oneTrustSettings as any;
    return {
        oneTrustScriptSrc: oneTrustSettingsCollection?.oneTrustScriptUrl,
        oneTrustDataDomainScript: oneTrustSettingsCollection?.oneTrustDataDomainScript,
        oneTrustEnabled: oneTrustSettingsCollection?.oneTrustActive,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface PrivacyChoicesStyles {
    container?: InjectableCss;
    privacyChoicesLink?: LinkPresentationProps;
}

export const privacyChoicesStyles: PrivacyChoicesStyles = {};

const PrivacyChoices = ({ oneTrustScriptSrc, oneTrustDataDomainScript, oneTrustEnabled }: Props) => {
    const styles = privacyChoicesStyles;
    // XNG-Changes
    if (getIsShareEntity()) {
        return null;
    }

    const status = useScript(oneTrustScriptSrc);

    if (!oneTrustEnabled || !oneTrustScriptSrc || !oneTrustDataDomainScript) {
        return null;
    }
    useEffect(() => {
        const script = document.querySelector(`script[src="${oneTrustScriptSrc}"]`);

        if (script && status === "loading") {
            script.setAttribute("data-domain-script", oneTrustDataDomainScript);
        }
    }, [status]);

    const oneTrustPrivacyChoicesModalHandler = () => {
        if (typeof window !== "undefined" && (window as any).OneTrust) {
            (window as any).OneTrust.ToggleInfoDisplay();
        }
    };
    return (
        <StyledWrapper className="one-trust">
            <Link id="ot-sdk-btn" {...styles.privacyChoicesLink} onClick={oneTrustPrivacyChoicesModalHandler}>
                {translate("Your Privacy Choices")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PrivacyChoices),
    definition: {
        group: XNGAGE_ELEMENTS,
    },
};
export default widgetModule;
